import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { landingPathFor } from './auth/landing-path';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RequireRole } from './auth/RequireRole';
import { AppShell } from './components/layout/AppShell';
import { SectionTabs, type SectionTab } from './components/layout/SectionTabs';
import { ChangePasswordPage } from './modules/auth/ChangePasswordPage';
import { LoginPage } from './modules/auth/LoginPage';
import { BoardBindingsPage } from './modules/board/BoardBindingsPage';
import { DirectionPage } from './modules/board/DirectionPage';
import { OperatingModelPage } from './modules/board/OperatingModelPage';
import { PresencePage } from './modules/board/PresencePage';
import { ProductBindingsPage } from './modules/board/ProductBindingsPage';
import { ProductsPage } from './modules/board/ProductsPage';
import { NotFoundPage } from './modules/not-found/NotFoundPage';
import { KpisPage } from './modules/kpis/KpisPage';
import { ReportsPage } from './modules/reports/ReportsPage';
import { TargetsPage } from './modules/targets/TargetsPage';
import { TrackerPage } from './modules/tracker/TrackerPage';
import { UsersPage } from './modules/users/UsersPage';

const BOARD_TABS: readonly SectionTab[] = [
  { to: '/board', labelKey: 'pages.board', end: true },
  { to: '/board/operating-model', labelKey: 'pages.operatingModel' },
  { to: '/board/products', labelKey: 'pages.products' },
  { to: '/board/presence', labelKey: 'pages.presence' },
];

const SETUP_TABS: readonly SectionTab[] = [
  { to: '/setup/kpis', labelKey: 'pages.setupKpis' },
  { to: '/setup/targets', labelKey: 'pages.setupTargets' },
  { to: '/setup/board-bindings', labelKey: 'pages.setupBoardBindings', roles: ['ADMIN', 'PM'] },
  { to: '/setup/product-bindings', labelKey: 'pages.setupProductBindings', roles: ['ADMIN', 'PM'] },
];

/** `/` → the role's landing page (plan §7, decision 19): the Board for every role. */
const HomeRedirect = () => {
  const { user } = useAuth();

  return (
    <Navigate
      to={user ? landingPathFor(user.role) : '/login'}
      replace
    />
  );
};

/** Every route of plan §7, inside `ProtectedRoute > AppShell` except the two sign-in pages. */
export const AppRouter = () => (
  <Routes>
    <Route
      path="/login"
      element={<LoginPage />}
    />

    <Route element={<ProtectedRoute />}>
      {/* Outside the shell: during the forced change there is nowhere else to go. */}
      <Route
        path="/change-password"
        element={<ChangePasswordPage />}
      />

      <Route element={<AppShell />}>
        <Route
          index
          element={<HomeRedirect />}
        />
        <Route
          path="/tracker"
          element={<TrackerPage />}
        />
        <Route
          path="/reports"
          element={<ReportsPage />}
        />

        <Route
          path="/board"
          element={<SectionTabs tabs={BOARD_TABS} />}
        >
          <Route
            index
            element={<DirectionPage />}
          />
          <Route
            path="operating-model"
            element={<OperatingModelPage />}
          />
          <Route
            path="products"
            element={<ProductsPage />}
          />
          <Route
            path="presence"
            element={<PresencePage />}
          />
        </Route>

        <Route
          path="/setup"
          element={<SectionTabs tabs={SETUP_TABS} />}
        >
          <Route
            index
            element={
              <Navigate
                to="/setup/kpis"
                replace
              />
            }
          />
          <Route
            path="kpis"
            element={<KpisPage />}
          />
          <Route
            path="targets"
            element={<TargetsPage />}
          />
          <Route
            path="board-bindings"
            element={
              <RequireRole roles={['ADMIN', 'PM']}>
                <BoardBindingsPage />
              </RequireRole>
            }
          />
          <Route
            path="product-bindings"
            element={
              <RequireRole roles={['ADMIN', 'PM']}>
                <ProductBindingsPage />
              </RequireRole>
            }
          />
        </Route>

        <Route
          path="/settings/users"
          element={
            <RequireRole roles={['ADMIN']}>
              <UsersPage />
            </RequireRole>
          }
        />
      </Route>
    </Route>

    <Route
      path="*"
      element={<NotFoundPage />}
    />
  </Routes>
);
